import { ChangeDetectionStrategy, Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import {
    COMMON_AV_PORTS_SPEC,
    PING_LIMITS,
    PING_SERVICE,
    PingResult,
    countPortsInSpec,
    describePort,
    maxPingCountFor,
    maxPortsFor
} from 'src/api/ping.service.interface';

/**
 * Ping / network diagnostics page, shared by the on-device dashboard and the cloud portal.
 *
 * The transport comes from the PING_SERVICE token — DevicePingService (SignalR to the
 * processor) or CloudPingService (REST to DeviceRegistry.Api, relayed over the device
 * tunnel). Either way the ping itself runs on the processor.
 *
 * Accepts ?host=<address> and ?autorun=1 so links from anywhere an IP is displayed land
 * here prefilled and already running.
 */
@Component({
    selector: 'app-ping',
    standalone: true,
    imports: [CommonModule, FormsModule, PageHeaderComponent, SectionCardComponent, EmptyStateComponent, InputTextModule, InputNumberModule, CheckboxModule, ButtonModule, TagModule, TooltipModule],
    templateUrl: './ping.component.html',
    styleUrl: './ping.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PingComponent implements OnInit {
    private pingService = inject(PING_SERVICE);
    private route = inject(ActivatedRoute);

    /** Cloud mode gets its header from the Device shell, so it opts out. */
    @Input() showHeader = true;

    readonly limits = PING_LIMITS;

    host = signal('');
    count = signal<number>(PING_LIMITS.defaultCount);
    timeoutMs = signal<number>(PING_LIMITS.defaultTimeoutMs);
    /** Port list/range spec, e.g. '22,80,443,8000-8010'. */
    ports = signal('');
    portTimeoutMs = signal<number>(PING_LIMITS.defaultPortTimeoutMs);
    resolveDns = signal(true);

    running = signal(false);
    result = signal<PingResult | null>(null);
    errorMessage = signal<string | null>(null);
    consoleExpanded = signal(true);

    /**
     * A longer per-packet timeout buys fewer packets, so the device keeps every run inside its
     * time budget. Mirror that here so the form can't ask for a request that gets clamped.
     */
    maxCount = computed(() => maxPingCountFor(this.timeoutMs() ?? PING_LIMITS.defaultTimeoutMs));

    /** How many ports the spec resolves to; null when it is malformed. */
    portCount = computed(() => countPortsInSpec(this.ports()));

    /** The most ports allowed at the chosen per-port timeout. */
    maxPorts = computed(() => maxPortsFor(this.portTimeoutMs() ?? PING_LIMITS.defaultPortTimeoutMs));

    /**
     * Explains why the port spec won't be accepted, so the user finds out here rather than from
     * a 400. Mirrors the API's rules.
     */
    portsError = computed(() => {
        if (!this.ports().trim()) return null;

        const count = this.portCount();
        if (count === null) return 'Use ports and ranges like 22,80,443,8000-8010.';

        const cap = this.maxPorts();
        if (count > cap) {
            return `${count} ports is too many — at most ${cap} at a ${this.portTimeoutMs()} ms per-port timeout. Lower the timeout or narrow the range.`;
        }
        return null;
    });

    canRun = computed(() => this.host().trim().length > 0 && !this.running() && !this.portsError());

    /** Summary shown beside the pass/fail badge. */
    summaryText = computed(() => {
        const result = this.result();
        if (!result) return '';

        const parts: string[] = [];
        const summary = result.ping?.summary;
        if (summary && summary.sent > 0) {
            parts.push(`${summary.received}/${summary.sent} replies`);
            if (summary.avgMs != null) parts.push(`avg ${summary.avgMs} ms`);
        }
        const scan = result.ports;
        if (scan && scan.scanned > 0) {
            parts.push(
                scan.scanned === 1
                    ? `port ${scan.results[0].port} ${scan.results[0].success ? 'open' : 'closed'}`
                    : `${scan.open}/${scan.scanned} ports open`
            );
        }
        if (result.resolvedAddress && result.resolvedAddress !== result.host) {
            parts.push(result.resolvedAddress);
        }
        return parts.join(' · ');
    });

    consoleText = computed(() => (this.result()?.lines ?? []).join('\n'));

    ngOnInit(): void {
        const params = this.route.snapshot.queryParamMap;

        const host = params.get('host');
        if (host) this.host.set(host);

        const ports = params.get('ports');
        if (ports) {
            this.ports.set(ports);
        } else {
            const port = Number(params.get('port'));
            if (Number.isInteger(port) && port > 0 && port <= 65535) this.ports.set(String(port));
        }

        if (params.get('autorun') === '1' && this.host().trim().length > 0) {
            void this.run();
        }
    }

    onTimeoutChange(value: number): void {
        this.timeoutMs.set(value);
        const cap = this.maxCount();
        if ((this.count() ?? 0) > cap) this.count.set(cap);
    }

    async run(): Promise<void> {
        if (!this.canRun()) return;

        this.running.set(true);
        this.errorMessage.set(null);
        this.result.set(null);

        try {
            const result = await this.pingService.networkPing({
                host: this.host().trim(),
                count: this.count() ?? PING_LIMITS.defaultCount,
                timeoutMs: this.timeoutMs() ?? PING_LIMITS.defaultTimeoutMs,
                ports: this.ports().trim(),
                portTimeoutMs: this.portTimeoutMs() ?? PING_LIMITS.defaultPortTimeoutMs,
                resolveDns: this.resolveDns()
            });

            this.result.set(result);
            // A result can carry an error even when the request itself succeeded — e.g. the
            // device refused because another test is already running.
            if (result?.error && !result.ping?.attempts?.length) {
                this.errorMessage.set(result.error);
            }
            this.consoleExpanded.set(true);
        } catch (err: unknown) {
            this.errorMessage.set(this.describeError(err));
        } finally {
            this.running.set(false);
        }
    }

    toggleConsole(): void {
        this.consoleExpanded.update((v) => !v);
    }

    /** Fills the port field with the curated AV port list. */
    useCommonAvPorts(): void {
        this.ports.set(COMMON_AV_PORTS_SPEC);
    }

    clearPorts(): void {
        this.ports.set('');
    }

    /** Only the ports that accepted a connection, for the results table. */
    openPorts = computed(() => (this.result()?.ports?.results ?? []).filter((p) => p.success));

    /** The usual service for a port, for the results table. */
    serviceFor(port: number): string {
        return describePort(port) ?? '';
    }

    private describeError(err: unknown): string {
        const candidate = err as { error?: { error?: string }; message?: string } | null;
        return candidate?.error?.error ?? candidate?.message ?? 'The ping request failed.';
    }
}
